/* eslint-disable no-nested-ternary */
import { useState, useEffect, ChangeEvent } from 'react';
import { default as React } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Paper,
  TextField, IconButton, Tooltip, CircularProgress, FormControlLabel, Switch
} from '@material-ui/core';
import { useApi, fetchApiRef, discoveryApiRef, alertApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Secret, DialogMode } from './types';
import { executeAction } from '../../utils/apiUtils';
import { SpikeAnnotations } from '@ciandtspike.io/plugin-ciandtspike-common';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import FileCopy from '@material-ui/icons/FileCopy';
import Warning from '@material-ui/icons/Warning';
import RemoveCircle from '@material-ui/icons/RemoveCircle';

interface SecretsDialogProps {
  open: boolean;
  mode: DialogMode;
  secret: Secret | null;
  onClose: () => void;
  onSave: () => void;
}

// Utility functions
const maskSecretValue = (value: string) => '•'.repeat(Math.min(value?.length || 0, 20));
const formatSecretValue = (value: string) => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};
const isJson = (value: string) => {
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === 'object' && parsed !== null;
  } catch {
    return false;
  }
};

const JsonFieldsEditor: React.FC<{
  fields: Record<string, string>;
  onKeyChange: (oldKey: string, newKey: string) => void;
  onValueChange: (key: string, value: string) => void;
  onRemove: (key: string) => void;
  onAdd: () => void;
  showJsonFields: Record<string, boolean>;
  onToggleVisibility: (key: string) => void;
  onCopy: (key: string, value: string) => void;
  copiedFields: Record<string, boolean>;
  disabled?: boolean;
}> = ({
  fields, onKeyChange, onValueChange, onRemove, onAdd,
  showJsonFields, onToggleVisibility, onCopy, copiedFields, disabled
}) => (
  <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
    {Object.entries(fields).map(([key, value], _idx) => (
      <Box key={key} display="flex" alignItems="center" mb={1}>
        <TextField
          label="Key"
          value={key}
          onChange={e => onKeyChange(key, e.target.value)}
          size="small"
          style={{ minWidth: 120, marginRight: 8 }}
          disabled={disabled}
        />
        <TextField
          label="Value"
          value={value}
          onChange={e => onValueChange(key, e.target.value)}
          type={showJsonFields[key] ? 'text' : 'password'}
          size="small"
          style={{ flex: 1, marginRight: 8 }}
          disabled={disabled}
        />
        <Tooltip title={showJsonFields[key] ? "Hide value" : "Show value"}>
          <IconButton
            aria-label={showJsonFields[key] ? "Hide value" : "Show value"}
            onClick={() => onToggleVisibility(key)}
            size="small"
            disabled={disabled}
          >
            {showJsonFields[key] ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </Tooltip>
        <Tooltip title={copiedFields[key] ? "Copied!" : "Copy value"}>
          <IconButton
            aria-label="Copy value"
            onClick={() => onCopy(key, value)}
            size="small"
            disabled={disabled}
          >
            <FileCopy />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove secret">
          <IconButton
            aria-label="Remove secret"
            onClick={() => onRemove(key)}
            size="small"
            disabled={disabled}
          >
            <RemoveCircle />
          </IconButton>
        </Tooltip>
      </Box>
    ))}
    <Box mt={2}>
      <Button variant="outlined" color="primary" onClick={onAdd} disabled={disabled}>
        Add Secret
      </Button>
    </Box>
  </Paper>
);

export const SecretsDialog: React.FC<SecretsDialogProps> = ({
  open, mode, secret, onClose, onSave,
}) => {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const alertApi = useApi(alertApiRef);
  const { entity } = useEntity();

  const isAwsProvider = entity?.metadata?.annotations?.[SpikeAnnotations.CLOUD_PROVIDER] === 'aws';

  // State
  const [showSecretValue, setShowSecretValue] = useState(false);
  const [showJsonFields, setShowJsonFields] = useState<Record<string, boolean>>({});
  const [copiedFields, setCopiedFields] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({ name: '', value: '' });
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  const [awsCreateAsJson, setAwsCreateAsJson] = useState(false);
  const [jsonFormFields, setJsonFormFields] = useState<Record<string, string>>({});

  // Effects
  useEffect(() => {
    if (open && secret) {
      setFormData({ name: secret.name || '', value: secret.value || '' });
      if (isJson(secret.value || '')) {
        setJsonFormFields(JSON.parse(secret.value || '{}'));
      } else {
        setJsonFormFields({});
      }
    } else if (open && mode === 'create') {
      setFormData({ name: '', value: '' });
      setJsonFormFields({});
      setAwsCreateAsJson(false);
    }
    if (open) setShowSecretValue(false);
  }, [open, secret, mode, isAwsProvider]);

  // Handlers
  const handleFormChange = (field: keyof typeof formData) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleJsonKeyChange = (oldKey: string, newKey: string) => {
    setJsonFormFields(prev => {
      const entries = Object.entries(prev).map(([k, v]) => (k === oldKey ? [newKey, v] : [k, v]));
      return Object.fromEntries(entries);
    });
  };

  const handleJsonValueChange = (key: string, value: string) => {
    setJsonFormFields(prev => {
      const entries = Object.entries(prev).map(([k, v]) => (k === key ? [k, value] : [k, v]));
      return Object.fromEntries(entries);
    });
  };

  const handleAddJsonField = () => {
    setJsonFormFields(prev => ({ ...prev, [`key${Object.keys(prev).length + 1}`]: '' }));
  };

  const handleRemoveJsonField = (key: string) => {
    setJsonFormFields(prev => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const toggleJsonFieldVisibility = (key: string) => {
    setShowJsonFields(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCopyJsonField = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value ?? '');
      setCopiedFields(prev => ({ ...prev, [key]: true }));
      alertApi.post({ message: `Field "${key}" copied to clipboard successfully`, severity: 'success', display: 'transient' });
      setTimeout(() => setCopiedFields(prev => ({ ...prev, [key]: false })), 1500);
    } catch {
      alertApi.post({ message: `Failed to copy field "${key}"`, severity: 'error', display: 'transient' });
    }
  };

  const toggleSecretVisibility = () => setShowSecretValue(prev => !prev);

  const handleCopySecret = async () => {
    try {
      const secretValue = mode === 'view' ? secret?.value : formData.value;
      await navigator.clipboard.writeText(secretValue ?? '');
      alertApi.post({ message: 'Secret value copied to clipboard successfully', severity: 'success', display: 'transient' });
    } catch {
      alertApi.post({ message: 'Failed to copy secret to clipboard', severity: 'error', display: 'transient' });
    }
  };

  const handleAction = async () => {
    if (!secret && mode !== 'create') return;
    setIsPerformingAction(true);
    try {
      let secretData;
      if (isAwsProvider && mode === 'create') {
        secretData = {
          ...formData,
          value: awsCreateAsJson ? JSON.stringify(jsonFormFields) : formData.value,
        };
      } else if (isAwsProvider && isJson(formData.value) && mode === 'edit') {
        secretData = { ...formData, value: JSON.stringify(jsonFormFields) };
      } else if (mode === 'create' || mode === 'edit') {
        secretData = { ...secret, ...formData };
      } else {
        secretData = secret;
      }
      await executeAction(mode, secretData, entity, fetchApi, discoveryApi);
      onSave();
    } catch (error) {
      alertApi.post({ message: `Error on ${mode} secret: ${String(error)}`, severity: 'error', display: 'transient' });
    } finally {
      setIsPerformingAction(false);
    }
  };

  // Validation
  const isAwsCreateValid = isAwsProvider && mode === 'create'
    ? !!formData.name && (
      awsCreateAsJson
        ? Object.keys(jsonFormFields).length > 0 && Object.values(jsonFormFields).every(v => v !== '')
        : !!formData.value
    )
    : true;

  // Renderers
  const renderActions = () => (
    <>
      <Button onClick={onClose} disabled={isPerformingAction}>
        {mode === 'view' ? 'Close' : 'Cancel'}
      </Button>
      {(mode === 'create' || mode === 'edit') && (
        <Button
          onClick={handleAction}
          color="primary"
          disabled={
            isAwsProvider && mode === 'create'
              ? !isAwsCreateValid || isPerformingAction
              : (!formData.name || !formData.value || isPerformingAction)
          }
          startIcon={isPerformingAction ? <CircularProgress size={16} /> : undefined}
        >
          {isPerformingAction ? 'Saving...' : 'Save'}
        </Button>
      )}
      {mode === 'delete' && (
        <Button
          onClick={handleAction}
          color="secondary"
          variant="contained"
          disabled={isPerformingAction}
          style={{ backgroundColor: '#f44336' }}
          startIcon={isPerformingAction ? <CircularProgress size={16} /> : undefined}
        >
          {isPerformingAction ? 'Deleting...' : 'Delete Secret'}
        </Button>
      )}
    </>
  );

  const renderContent = () => {
    if (mode === 'delete') {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
          <Warning style={{ fontSize: 48, color: '#ff9800', marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom>
            Are you sure you want to delete this secret?
          </Typography>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            This action cannot be undone. The secret will be permanently removed.
          </Typography>
          <Box mt={2} p={2} borderRadius={4} width="100%">
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              Secret Name: <span style={{ fontFamily: 'monospace' }}>{secret?.name}</span>
            </Typography>
          </Box>
        </Box>
      );
    }

    if (mode === 'create' && isAwsProvider) {
      return (
        <>
          <TextField
            label="Secret Name"
            value={formData.name}
            onChange={handleFormChange('name')}
            fullWidth
            margin="normal"
            required
            disabled={isPerformingAction}
            placeholder="Enter secret name"
          />
          <FormControlLabel
            control={
              <Switch
                checked={awsCreateAsJson}
                onChange={(_, checked) => setAwsCreateAsJson(checked)}
                color="primary"
                disabled={isPerformingAction}
              />
            }
            label={awsCreateAsJson ? "Key/Value pairs" : "Raw string"}
            style={{ marginBottom: 16 }}
          />
          {awsCreateAsJson ? (
            <JsonFieldsEditor
              fields={jsonFormFields}
              onKeyChange={handleJsonKeyChange}
              onValueChange={handleJsonValueChange}
              onRemove={handleRemoveJsonField}
              onAdd={handleAddJsonField}
              showJsonFields={showJsonFields}
              onToggleVisibility={toggleJsonFieldVisibility}
              onCopy={handleCopyJsonField}
              copiedFields={copiedFields}
              disabled={isPerformingAction}
            />
          ) : (
            <TextField
              label="Secret Value"
              value={formData.value}
              onChange={handleFormChange('value')}
              type={showSecretValue ? 'text' : 'password'}
              fullWidth
              margin="normal"
              required
              disabled={isPerformingAction}
              multiline={showSecretValue}
              placeholder="Enter secret value"
              InputProps={{
                endAdornment: (
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Tooltip title={showSecretValue ? "Hide secret value" : "Show secret value"}>
                      <IconButton
                        aria-label={showSecretValue ? "Hide secret value" : "Show secret value"}
                        onClick={toggleSecretVisibility}
                        edge="end"
                        disabled={isPerformingAction}
                        size="small"
                      >
                        {showSecretValue ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy secret value">
                      <IconButton
                        aria-label="Copy secret value"
                        onClick={handleCopySecret}
                        edge="end"
                        disabled={isPerformingAction}
                        size="small"
                        style={{ marginTop: 4 }}
                      >
                        <FileCopy />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ),
              }}
            />
          )}
          {isPerformingAction && (
            <Box display="flex" alignItems="center" mt={2}>
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              <Typography variant="body2" color="textSecondary">
                Creating secret...
              </Typography>
            </Box>
          )}
        </>
      );
    }

    if (mode === 'edit') {
      const jsonMode = isJson(formData.value);
      let jsonObj: Record<string, any> = {};
      if (jsonMode) {
        try {
          jsonObj = JSON.parse(formData.value);
        } catch {
          jsonObj = {};
        }
      }
      return (
        <>
          <TextField
            label="Secret Name"
            value={formData.name}
            disabled
            fullWidth
            margin="normal"
            required
          />
          {jsonMode ? (
            isAwsProvider ? (
              <JsonFieldsEditor
                fields={jsonFormFields}
                onKeyChange={handleJsonKeyChange}
                onValueChange={handleJsonValueChange}
                onRemove={handleRemoveJsonField}
                onAdd={handleAddJsonField}
                showJsonFields={showJsonFields}
                onToggleVisibility={toggleJsonFieldVisibility}
                onCopy={handleCopyJsonField}
                copiedFields={copiedFields}
                disabled={isPerformingAction}
              />
            ) : (
              <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
                {Object.entries(jsonObj).map(([key, value]) => (
                  <Box key={key} display="flex" alignItems="center" mb={1}>
                    <Typography variant="body2" style={{ fontWeight: 'bold', minWidth: 120 }}>
                      {key}:
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        flex: 1,
                        marginRight: 8,
                        userSelect: showJsonFields[key] ? 'text' : 'none'
                      }}
                    >
                      {showJsonFields[key] ? String(value) : maskSecretValue(String(value))}
                    </Typography>
                    <Tooltip title={showJsonFields[key] ? "Hide value" : "Show value"}>
                      <IconButton
                        aria-label={showJsonFields[key] ? "Hide value" : "Show value"}
                        onClick={() => toggleJsonFieldVisibility(key)}
                        size="small"
                        disabled={isPerformingAction}
                      >
                        {showJsonFields[key] ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={copiedFields[key] ? "Copied!" : "Copy value"}>
                      <IconButton
                        aria-label="Copy value"
                        onClick={() => handleCopyJsonField(key, String(value))}
                        size="small"
                        disabled={isPerformingAction}
                      >
                        <FileCopy />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Paper>
            )
          ) : (
            <TextField
              label="Secret Value"
              value={formData.value}
              onChange={handleFormChange('value')}
              type={showSecretValue ? 'text' : 'password'}
              fullWidth
              margin="normal"
              required
              disabled={isPerformingAction}
              multiline={showSecretValue}
              placeholder="Update secret value"
              InputProps={{
                endAdornment: (
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Tooltip title={showSecretValue ? "Hide secret value" : "Show secret value"}>
                      <IconButton
                        aria-label={showSecretValue ? "Hide secret value" : "Show secret value"}
                        onClick={toggleSecretVisibility}
                        edge="end"
                        disabled={isPerformingAction}
                        size="small"
                      >
                        {showSecretValue ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy secret value">
                      <IconButton
                        aria-label="Copy secret value"
                        onClick={handleCopySecret}
                        edge="end"
                        disabled={isPerformingAction}
                        size="small"
                        style={{ marginTop: 4 }}
                      >
                        <FileCopy />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ),
              }}
            />
          )}
          {isPerformingAction && (
            <Box display="flex" alignItems="center" mt={2}>
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              <Typography variant="body2" color="textSecondary">
                Updating secret...
              </Typography>
            </Box>
          )}
        </>
      );
    }

    if (mode === 'create' && !isAwsProvider) {
      return (
        <>
          <TextField
            label="Secret Name"
            value={formData.name}
            onChange={handleFormChange('name')}
            fullWidth
            margin="normal"
            required
            disabled={isPerformingAction}
            placeholder="Enter secret name"
          />
          <TextField
            label="Secret Value"
            value={formData.value}
            onChange={handleFormChange('value')}
            type={showSecretValue ? 'text' : 'password'}
            fullWidth
            margin="normal"
            required
            disabled={isPerformingAction}
            multiline={showSecretValue}
            placeholder="Enter secret value"
            InputProps={{
              endAdornment: (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Tooltip title={showSecretValue ? "Hide secret value" : "Show secret value"}>
                    <IconButton
                      aria-label={showSecretValue ? "Hide secret value" : "Show secret value"}
                      onClick={toggleSecretVisibility}
                      edge="end"
                      disabled={isPerformingAction}
                      size="small"
                    >
                      {showSecretValue ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy secret value">
                    <IconButton
                      aria-label="Copy secret value"
                      onClick={handleCopySecret}
                      edge="end"
                      disabled={isPerformingAction}
                      size="small"
                      style={{ marginTop: 4 }}
                    >
                      <FileCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
              ),
            }}
          />
          {isPerformingAction && (
            <Box display="flex" alignItems="center" mt={2}>
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              <Typography variant="body2" color="textSecondary">
                Creating secret...
              </Typography>
            </Box>
          )}
        </>
      );
    }

    // View mode
    const jsonMode = isJson(secret?.value ?? '');
    let jsonObj: Record<string, any> = {};
    if (jsonMode) {
      try {
        jsonObj = JSON.parse(secret?.value ?? '{}');
      } catch {
        jsonObj = {};
      }
    }
    if (jsonMode) {
      return (
        <Box>
          <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
            {Object.entries(jsonObj).map(([key, value]) => (
              <Box key={key} display="flex" alignItems="center" mb={1}>
                <Typography variant="body2" style={{ fontWeight: 'bold', minWidth: 120 }}>
                  {key}:
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    flex: 1,
                    marginRight: 8,
                    userSelect: showJsonFields[key] ? 'text' : 'none'
                  }}
                >
                  {showJsonFields[key] ? String(value) : maskSecretValue(String(value))}
                </Typography>
                <Tooltip title={showJsonFields[key] ? "Hide value" : "Show value"}>
                  <IconButton
                    aria-label={showJsonFields[key] ? "Hide value" : "Show value"}
                    onClick={() => toggleJsonFieldVisibility(key)}
                    size="small"
                  >
                    {showJsonFields[key] ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={copiedFields[key] ? "Copied!" : "Copy value"}>
                  <IconButton
                    aria-label="Copy value"
                    onClick={() => handleCopyJsonField(key, String(value))}
                    size="small"
                  >
                    <FileCopy />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Paper>
        </Box>
      );
    }
    const secretValue = secret?.value || '';
    const displayValue = showSecretValue ? formatSecretValue(secretValue) : maskSecretValue(secretValue);

    return (
      <Box display="flex" alignItems="flex-start" marginBottom={2}>
        <Paper
          variant="outlined"
          style={{
            padding: 16,
            minHeight: 10,
            maxHeight: 300,
            overflow: 'auto',
            flex: 1,
          }}
        >
          <Typography
            variant="body2"
            color="textSecondary"
            style={{
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: 14,
              lineHeight: '1.4',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              userSelect: showSecretValue ? 'text' : 'none',
            }}
          >
            {displayValue}
          </Typography>
        </Paper>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Tooltip title={showSecretValue ? "Hide secret value" : "Show secret value"}>
            <IconButton
              aria-label={showSecretValue ? "Hide secret value" : "Show secret value"}
              onClick={toggleSecretVisibility}
              color="primary"
              size="small"
            >
              {showSecretValue ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </Tooltip>
          {showSecretValue && secretValue && (
            <Tooltip title="Copy secret value to clipboard">
              <IconButton
                aria-label="Copy secret value to clipboard"
                onClick={handleCopySecret}
                color="primary"
                size="small"
              >
                <FileCopy />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={isPerformingAction ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Create New Secret' :
          mode === 'edit' ? `Edit ${secret?.name}` :
            mode === 'delete' ? 'Delete Secret' : secret?.name}
      </DialogTitle>
      <DialogContent>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
};
